import { EnvConstant, PathConstant } from "@/const";
import { STT_OK } from "@/const/api.const";
import { profileServices } from "@/features/profile/services/profile.service";
import {
  DetailIcon,
  DocumentIcon,
  EditIcon,
  MenuIcon,
  PlusIcon,
  StatisticalIcon,
  UserIcon,
  WindowPlusIcon,
} from "@/icons";
import { Box } from "@mui/material";
import clsx from "clsx";
import Link from "next/link";
import { ReactNode, useEffect, useState } from "react";

type ProfileLink = {
  text: string;
  href: string;
  icon: ReactNode;
  isExternal?: boolean;
};

const NUTRITION_LINK_TEXT = "Link dinh dưỡng";

const profileLinks: ProfileLink[] = [
  {
    text: "Thông tin",
    href: PathConstant.PROFILE,
    icon: <DetailIcon />,
  },
  {
    text: "Mua hàng",
    href: PathConstant.PROFILE_PURCHASE_PACKAGE,
    icon: <WindowPlusIcon />,
  },
  {
    text: "Chỉnh sửa thông tin",
    href: PathConstant.PROFILE_EDIT_INFO,
    icon: <EditIcon />,
  },
  {
    text: "Rút điểm",
    href: PathConstant.PROFILE_WITHDRAW_POINTS,
    icon: <StatisticalIcon />,
  },
  {
    text: "Danh sách điểm",
    href: PathConstant.PROFILE_REWARD_HISTORY,
    icon: <MenuIcon />,
  },
  {
    text: "Danh sách thành viên",
    href: PathConstant.PROFILE_MEMBERS_STRUCTURE,
    icon: <UserIcon />,
  },
  {
    text: "Danh sách CTV",
    href: PathConstant.PROFILE_MEMBERS_GENERATION,
    icon: <UserIcon />,
  },
  {
    text: "Thêm CTV",
    href: PathConstant.PROFILE_MEMBERS_CREATE,
    icon: <PlusIcon />,
  },
  {
    text: "Lịch sử điểm thưởng",
    href: PathConstant.PROFILE_BONUS_HISTORY,
    icon: <DocumentIcon />,
  },
  {
    text: "Mua sản phẩm",
    href: PathConstant.PROFILE_PRODUCTS_PURCHASE,
    icon: <WindowPlusIcon />,
  },
  {
    text: "Danh sách sản phẩm đã mua",
    href: PathConstant.PROFILE_PRODUCTS,
    icon: <DocumentIcon />,
  },
  {
    text: NUTRITION_LINK_TEXT,
    href: EnvConstant.NEXT_PUBLIC_NUTRITION_LINK_URL,
    icon: <DocumentIcon />,
    isExternal: true,
  },
];

type SidebarNavProps = {
  isMobile: boolean;
  isOpen: boolean;
  onNavigate: () => void;
};

const SidebarNav = ({ isMobile, isOpen, onNavigate }: SidebarNavProps) => {
  const [showNutritionLink, setShowNutritionLink] = useState(true);
  const [showTopupLink, setShowTopupLink] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const handleGetGeneralSettings = async () => {
      const response = await profileServices.getGeneralSettings();

      if (!isMounted || response.status !== STT_OK || !response.payload.data) {
        return;
      }

      setShowNutritionLink(response.payload.data.showNutritionLink);
      setShowTopupLink(response.payload.data.showTopupLink);
    };

    handleGetGeneralSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  const visibleLinks = showNutritionLink
    ? profileLinks
    : profileLinks.filter((item) => item.text !== NUTRITION_LINK_TEXT);

  return (
    <Box
      className={clsx(
        "main-left flex flex-col w-[270px] bg-white transition-transform",
        {
          "translate-x-0": !isMobile || isOpen,
          "-translate-x-full": isMobile && !isOpen,
        },
      )}
    >
      <ul className="m-0 p-2">
        {visibleLinks.map((item) => (
          <li key={`${item.text}-${item.href}`} className="list-none">
            <Link
              href={item.href}
              className="flex items-center gap-3 border-b-[1px] border-light p-2"
              onClick={onNavigate}
              target={item.isExternal ? "_blank" : undefined}
              rel={item.isExternal ? "noopener noreferrer" : undefined}
            >
              <Box className="flex shrink-0 items-center justify-center [&>svg]:size-5">
                {item.icon}
              </Box>
              <span>{item.text}</span>
            </Link>
          </li>
        ))}
      </ul>

      {showTopupLink && (
        <Box className="p-2">
          <Link
            href={PathConstant.PROFILE_TOPUP}
            onClick={onNavigate}
            className="flex items-center justify-center rounded-lg border-2 border-error p-2 font-semibold text-error"
          >
            Nạp
          </Link>
        </Box>
      )}
    </Box>
  );
};

export default SidebarNav;
